import { useEffect } from "react";

interface PrivacyPolicyModalProps {
  open: boolean;
  onClose: () => void;
}

const SECTIONS: { heading: string; body: string[] }[] = [
  {
    heading: "1. Who this applies to",
    body: [
      "SellSynq (sellsynq.com) is an AI sales revenue intelligence and meeting assistant. This notice explains what personal data SellSynq collects, why, how long it is kept, who it is shared with, and the rights you and meeting participants have under GDPR and CCPA.",
    ],
  },
  {
    heading: "2. What we collect",
    body: [
      "• Account data: your name, email, role/team, language and coaching preferences, and (optionally) an API key you provide.",
      "• Meeting data: meeting titles, the company/account you're meeting, participant names and emails (including from a connected Google Calendar), goals/notes, transcripts, live hints, sentiment, summaries, follow-up questions, action items, and any screenshots/infographics you generate.",
      "• Derived insights: aggregated product feedback / voice-of-the-field signals extracted from meetings.",
      "• Operational data: sign-in activity and client error reports used to keep the tool working.",
    ],
  },
  {
    heading: "3. Why we process it (purpose)",
    body: [
      "To transcribe and summarize your meetings, surface real-time coaching hints, prepare follow-ups, and produce aggregate insights for revenue teams. We process data only for these internal purposes and do not sell personal data.",
    ],
  },
  {
    heading: "4. Consent for participants",
    body: [
      "If a meeting includes other people, the rep running SellSynq is responsible for informing participants that the session is transcribed and analyzed, and for obtaining any consent required by law or policy before the meeting. SellSynq records an auditable acknowledgement when the rep confirms participants were notified.",
    ],
  },
  {
    heading: "5. Third parties that process data",
    body: [
      "To provide the service, limited data is sent to: Google Vertex AI / Gemini (transcription, hints, summaries), Google Cloud Speech-to-Text, Google Calendar/Drive (with your OAuth grant), Apollo.io and Brandfetch/Clearbit (company enrichment/logos, by company name/domain only), and an internal File Search knowledge base. Personal data is not sold.",
    ],
  },
  {
    heading: "6. Retention",
    body: [
      "Summaries are kept until you delete them. Transcripts follow your \"keep transcripts\" setting — when turned off, transcripts are deleted after the summary is generated (and existing transcripts are removed when you turn it off). You can delete any meeting, or your entire account, at any time.",
    ],
  },
  {
    heading: "7. Your rights (GDPR / CCPA)",
    body: [
      "You can access and export all your data (Settings → Your data → Export), correct it, and delete individual meetings or your whole account (Settings → Your data → Delete). Deleting a meeting or account permanently removes the associated transcripts, summaries, hints, screenshots and derived signals. For data-subject requests about a meeting participant, the rep who ran the meeting can execute the deletion; for help or escalation contact support@sellsynq.com.",
    ],
  },
];

/** Privacy notice, opened from the app footer and the sign-in screen. Mirrors the
 * TermsOfUseModal structure. */
export function PrivacyPolicyModal({ open, onClose }: PrivacyPolicyModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-back" style={{ zIndex: 1100 }} onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3 className="modal-title">SellSynq Privacy Notice (sellsynq.com)</h3>
            <p className="modal-sub">How SellSynq handles your data (GDPR / CCPA).</p>
          </div>
        </div>

        <div className="modal-body" style={{ maxHeight: "68vh", overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {SECTIONS.map((s) => (
              <div key={s.heading}>
                <div
                  style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", marginBottom: 4 }}
                >
                  {s.heading}
                </div>
                {s.body.map((para, i) => (
                  <p
                    key={`${s.heading}-${i}`}
                    style={{
                      fontSize: 13,
                      color: "var(--text-2)",
                      lineHeight: 1.55,
                      margin: i === 0 ? 0 : "6px 0 0",
                      paddingLeft: para.startsWith("•") ? 10 : 0,
                    }}
                  >
                    {para}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="pill-btn primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
